
import axios, { AxiosError, AxiosResponse } from "axios"
import { WorkflowsTableRow } from "@/types/workflow"
import { CreatedWorkflowResponse, WorkflowCreateRequestBodyV2 } from "@/types/WorkflowCreate/workflowCreate"
import { config } from "@/config"
import { TCSAPIResponse } from "@/types/types"
import { formatResponse } from "./helpers/formatResponse"
import { ActionsAndSubjectsResponse } from "@/types/WorkflowCreate/AppConfiguration"
import { WorkflowDetailsRespV2 } from "@/types/WorkflowCreate/workflowDetailsV2"
import { WorkflowUpdateRequestBodyV2 } from "@/types/WorkflowCreate/workflowUpdateV2"

export const WorkflowAPI = {
  getAll: async (token: string) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v2/workflows`,
        headers: {
          Authorization: `Bearer ${token}`
        },
      })

      return formatResponse<WorkflowsTableRow[]>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<WorkflowsTableRow[]>(null, error as AxiosError)
    }
  },

  getSingleWorkflow: async (token: string, workflowId: string) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v2/workflows/${workflowId}`,
        headers: {
          Authorization: `Bearer ${token}`
        },
      })

      return formatResponse<WorkflowDetailsRespV2>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<WorkflowDetailsRespV2>(null, error as AxiosError)
    }
  },

  createWorkflow: async (
    token: string,
    body: WorkflowCreateRequestBodyV2
  ) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v2/workflows`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: body
      })

      return formatResponse<CreatedWorkflowResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<CreatedWorkflowResponse>(null, error as AxiosError)
    }
  },

  saveDraftWorkflow: async (
    token: string,
    body: WorkflowCreateRequestBodyV2
  ) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v2/workflows/draft`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: body
      })

      return formatResponse<CreatedWorkflowResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<CreatedWorkflowResponse>(null, error as AxiosError)
    }
  },

  publishDraftWorkflow: async (
    token: string,
    workflowId: string
  ) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v2/workflows/${workflowId}/publish`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return formatResponse<TCSAPIResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<TCSAPIResponse>(null, error as AxiosError)
    }
  },

  updateWorkflow: async (
    token: string,
    workflowId: string,
    body: WorkflowUpdateRequestBodyV2
  ) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'PATCH',
        url: `${config.apiUrl}/v2/workflows/${workflowId}`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: body
      })

      return formatResponse<TCSAPIResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<TCSAPIResponse>(null, error as AxiosError)
    }
  },

  updateDraftWorkflow: async (
    token: string,
    workflowId: string,
    body: WorkflowUpdateRequestBodyV2
  ) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'PATCH',
        url: `${config.apiUrl}/v2/workflows/draft/${workflowId}`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: body
      })

      return formatResponse<TCSAPIResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<TCSAPIResponse>(null, error as AxiosError)
    }
  },

  changeWorkflowStatus: async (
    token: string,
    workflowId: string
  ) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'PATCH',
        url: `${config.apiUrl}/v1/workflows/${workflowId}/status`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return formatResponse<TCSAPIResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<TCSAPIResponse>(null, error as AxiosError)
    }
  },

  deleteWorkflow: async (
    token: string,
    workflowId: string
  ) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'DELETE',
        url: `${config.apiUrl}/v1/workflows/${workflowId}`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return formatResponse<TCSAPIResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<TCSAPIResponse>(null, error as AxiosError)
    }
  },

  runWorkflow: async (
    token: string,
    workflowId: string
  ) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/workflows/${workflowId}/run`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return formatResponse<TCSAPIResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<TCSAPIResponse>(null, error as AxiosError)
    }
  },

  getActionsAndSubjectsByApp: async (
    token: string,
    appId: string
  ) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/applications/${appId}/all_subjects`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return formatResponse<ActionsAndSubjectsResponse[]>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<ActionsAndSubjectsResponse[]>(null, error as AxiosError)
    }
  },

}
